import { createFileRoute } from '@tanstack/react-router';
import { SHIRES } from '../components/shared/electorate-shires';
import { SHIRE_CONTENT } from '../data/shire-content';

export const Route = createFileRoute('/preview/shires')({
  component: ShireContentPreview,
  head: () => ({
    meta: [
      { title: 'Preview — Shire content (raw)' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
});

function ShireContentPreview() {
  return (
    <main
      id="main-content"
      style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
          color: 'var(--green)',
          marginBottom: '0.25rem',
        }}
      >
        Shire content — raw preview
      </h1>
      <p style={{ color: 'var(--ink-mid)', fontStyle: 'italic', marginBottom: 'var(--sp-lg)' }}>
        Local QA view of every field in <code>src/data/shire-content.ts</code>. Not linked from the
        public site; <code>noindex</code>.
      </p>

      <nav
        aria-label="Jump to shire"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.4rem',
          marginBottom: 'var(--sp-lg)',
          padding: '0.6rem 0.8rem',
          background: 'var(--gold-pale, #faf3dc)',
          border: '1px solid var(--rule)',
          borderRadius: 8,
        }}
      >
        {SHIRES.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            style={{
              color: 'var(--green)',
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            {s.name}
          </a>
        ))}
      </nav>

      {SHIRES.map((shire) => {
        const c = SHIRE_CONTENT[shire.id];
        return (
          <section
            key={shire.id}
            id={shire.id}
            style={{
              marginBottom: 'var(--sp-xl)',
              padding: '1rem 1.1rem',
              background: 'var(--cream)',
              border: '1px solid var(--rule)',
              borderRadius: 8,
            }}
          >
            <header style={{ marginBottom: '0.75rem' }}>
              <div style={labelStyle}>id</div>
              <code style={{ fontSize: '0.85rem', color: 'var(--ink-mid)' }}>{shire.id}</code>
              <h2
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.5rem',
                  color: 'var(--green)',
                  margin: '0.2rem 0 0',
                }}
              >
                {shire.name}
              </h2>
            </header>

            <Field label="tagline">
              <p style={textStyle}>{c.tagline}</p>
              <Meta>{c.tagline.length} chars</Meta>
            </Field>

            <Field label={`paragraphs (${c.paragraphs.length})`}>
              <ol style={{ paddingLeft: '1.2rem', margin: 0, display: 'grid', gap: '0.6rem' }}>
                {c.paragraphs.map((p, i) => (
                  <li key={i} style={textStyle}>
                    {p}
                    <Meta>
                      {p.length} chars · {p.split(/\s+/).filter(Boolean).length} words
                    </Meta>
                  </li>
                ))}
              </ol>
            </Field>

            <Field label={`highlights (${c.highlights.length})`}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.3rem' }}>
                {c.highlights.map((h) => (
                  <li key={h.label} style={textStyle}>
                    <strong>{h.label}:</strong> {h.value}
                  </li>
                ))}
              </ul>
            </Field>

            <Field label="didYouKnow">
              <p style={textStyle}>{c.didYouKnow}</p>
              <Meta>{c.didYouKnow.length} chars</Meta>
            </Field>

            <Field label={`mainLocalities (${c.mainLocalities.length})`}>
              <p style={textStyle}>{c.mainLocalities.join(' · ')}</p>
            </Field>
          </section>
        );
      })}
    </main>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--ink-mid)',
  marginBottom: 2,
};

const textStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.95rem',
  lineHeight: 1.55,
  color: 'var(--ink)',
  whiteSpace: 'pre-wrap',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: '0.85rem' }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </div>
  );
}

function Meta({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: 'var(--ink-mid)', marginTop: 2 }}>{children}</div>
  );
}
